import type {
  AppliedStrategy,
  Cadence,
  CreateAppliedStrategy,
  EstrategiaEnBorradorBody,
  GroupDraft,
  OnOverlap,
  StrategyKind,
} from '../../types/strategy-applications.types';
import { PERMISO_INICIAL, type Permiso } from '../permiso-de-estrategia';

/**
 * La estrategia mientras se arma en los tres pasos (plan 3, Tarea 5). Todo lo
 * que la pantalla manda al backend sale de acá, con funciones puras que se
 * prueban solas: el alta (`POST /api/strategy-applications`), la simulación en
 * borrador (`POST /api/strategy-applications/simular`, 4B-datos T10) y la lista
 * de los que entran (`POST /api/strategy-applications/productos`).
 */

/** Cómo se dice a quién alcanza: lo que el producto ES, nunca un estado que cambia. */
export type FormaDelAlcance = 'CATEGORIA_Y_MARCA' | 'PRODUCTOS';

export interface BorradorDeEstrategia {
  nombre: string;
  forma: FormaDelAlcance;
  categorias: string[];
  marcas: string[];
  /** Con la forma PRODUCTOS: los SKUs elegidos. */
  skus: string[];
  /** Con la forma CATEGORIA_Y_MARCA: los que se sacan a mano. */
  excluidos: string[];
  metodo: StrategyKind;
  /** El número del método, como se escribió (vacío si el método no pide número). */
  valor: string;
  /** `null` = el umbral escrito no sirve: no deja crear. */
  permiso: Permiso | null;
  cadencia: Cadence;
  hora: number;
  minuto: number;
  dia: number;
  /** `YYYY-MM-DD`, o `null` = corre hasta que la apaguen. */
  hastaCuando: string | null;
  /** Qué hacer con los que ya tiene otra estrategia. `null` = todavía no se eligió. */
  onOverlap: OnOverlap | null;
}

export const BORRADOR_VACIO: BorradorDeEstrategia = {
  nombre: '',
  forma: 'CATEGORIA_Y_MARCA',
  categorias: [],
  marcas: [],
  skus: [],
  excluidos: [],
  metodo: 'MATCH_LOWEST',
  valor: '',
  permiso: PERMISO_INICIAL,
  cadencia: 'DAILY',
  hora: 6,
  minuto: 0,
  dia: 1,
  hastaCuando: null,
  onOverlap: null,
};

/**
 * Qué número pide cada método (verificado en `price-calculator.service.ts`): los
 * demás no leen nada de `strategyConfig`. El quinto método no tiene valor por
 * defecto en el backend (plan 3, P3-D1): el 10 es sólo el punto de partida.
 */
export const NUMERO_DEL_METODO: Partial<
  Record<
    StrategyKind,
    {
      key: 'undercutPercent' | 'targetMargin' | 'discountPercent';
      label: string;
      inicial: number;
      minimo: number;
    }
  >
> = {
  UNDERCUT_PERCENT: {
    key: 'undercutPercent',
    label: 'Cuánto por debajo (%)',
    inicial: 5,
    minimo: 0,
  },
  MARGIN_TARGET: {
    key: 'targetMargin',
    // Pulido (spec 232, vault 231): el margen es siempre sobre el precio de venta.
    label: 'Margen objetivo sobre el precio de venta (%)',
    inicial: 25,
    minimo: 0,
  },
  DISCOUNT_PERCENT: { key: 'discountPercent', label: 'Cuánto bajar (%)', inicial: 10, minimo: 1 },
};

export function valorInicial(metodo: StrategyKind): string {
  const n = NUMERO_DEL_METODO[metodo];
  return n ? String(n.inicial) : '';
}

/**
 * Escaneo previo Nº 8: los métodos que no leen la competencia (la misma lista que
 * `necesitaCompetencia` del backend, T4). «En cuanto lea la competencia»
 * (`INSTANT`) sólo corre cuando llega una lectura (`competitor-change-listener.ts`):
 * con éstos, en un catálogo sin competencia —el día uno—, no correría nunca.
 */
const NO_LEEN_LA_COMPETENCIA: readonly StrategyKind[] = ['DISCOUNT_PERCENT', 'MARGIN_TARGET'];
export const leeLaCompetencia = (metodo: StrategyKind): boolean =>
  !NO_LEEN_LA_COMPETENCIA.includes(metodo);

/** El número escrito, si sirve para ese método (entre su mínimo y 99). */
export function numeroValido(metodo: StrategyKind, valor: string): number | null {
  const n = NUMERO_DEL_METODO[metodo];
  if (!n) return null;
  const x = Number(valor.replace(',', '.'));
  return valor.trim() !== '' && Number.isFinite(x) && x >= n.minimo && x < 100 ? x : null;
}

export function configDelBorrador(b: BorradorDeEstrategia): Record<string, number> | null {
  const n = NUMERO_DEL_METODO[b.metodo];
  if (!n) return null;
  const x = numeroValido(b.metodo, b.valor);
  return x == null ? null : { [n.key]: x };
}

/**
 * El grupo de los PEDIDOS (los que entran, `/preview`, `/simular`): el mismo que
 * el del alta pero con un nombre fijo (27-sep, B5; el diálogo viejo lo hacía así,
 * `applied-strategies-tab.tsx:558`). El nombre no cambia a quién alcanza, y si
 * entrara en la clave, cada tecla volvería a pedir (todo `/api/strategy-applications`
 * pasa por el `writeLimiter`).
 */
export function grupoParaContar(b: BorradorDeEstrategia): GroupDraft | null {
  const g = grupoDelBorrador(b);
  return g ? { ...g, name: 'Borrador' } : null;
}

/** El grupo que va al backend en el alta. `null` = todavía no alcanza a nadie. */
export function grupoDelBorrador(b: BorradorDeEstrategia): GroupDraft | null {
  const name = b.nombre.trim() || 'Estrategia sin nombre';
  if (b.forma === 'PRODUCTOS') {
    return b.skus.length > 0 ? { name, kind: 'MANUAL', skus: b.skus } : null;
  }
  if (b.categorias.length === 0 && b.marcas.length === 0) return null;
  const filter: Record<string, unknown> = {};
  // Una lista de categorías vacía es un error para el resolvedor: sólo se manda con algo.
  if (b.categorias.length > 0) filter.categories = b.categorias;
  if (b.marcas.length > 0) filter.brands = b.marcas;
  return {
    name,
    kind: 'DYNAMIC',
    filter,
    ...(b.excluidos.length > 0 && { excludedSkus: b.excluidos }),
  };
}

/** Lo que falta para pasar de cada paso, dicho para la persona. Vacío = se puede seguir. */
export function faltaEnElPaso(b: BorradorDeEstrategia, paso: 1 | 2 | 3): string[] {
  const falta: string[] = [];
  if (paso >= 1) {
    if (!b.nombre.trim()) falta.push('Ponele un nombre a la estrategia.');
    if (!grupoDelBorrador(b)) {
      falta.push(
        b.forma === 'PRODUCTOS'
          ? 'Elegí al menos un producto.'
          : 'Elegí al menos una categoría o una marca.'
      );
    }
  }
  if (paso >= 2 && NUMERO_DEL_METODO[b.metodo] && numeroValido(b.metodo, b.valor) == null) {
    const n = NUMERO_DEL_METODO[b.metodo]!;
    falta.push(`${n.label.replace(' (%)', '')}: entre ${n.minimo} y 99 %.`);
  }
  if (paso >= 3) {
    if (b.permiso == null) falta.push('El umbral va de 1 a 100 %.');
    if (b.hastaCuando != null && !(finDelDia(b.hastaCuando) > Date.now())) {
      falta.push('La fecha de fin tiene que ser posterior a hoy.');
    }
  }
  return falta;
}

/**
 * `YYYY-MM-DD` → el último segundo de ese día en hora de Bolivia (UTC−4, sin
 * horario de verano), sea cual sea la zona de la computadora (ronda final B2).
 * La validación de «posterior a hoy» usa la misma cuenta.
 */
function finDelDia(fecha: string): number {
  return new Date(`${fecha}T23:59:59-04:00`).getTime();
}

/**
 * El cuerpo de `POST /api/strategy-applications/simular` (4B-datos T10). `null` = no pedir.
 *
 * 🔴 27-sep (B2, ruling R4): sin elegir qué hacer con los que ya tiene otra, se
 * manda `DEJARLOS` —como el diálogo viejo—: el backend sin `onOverlap` los
 * CUENTA, y el aviso de solapados del paso 3 (paso-3-cuando.tsx) dice que
 * «si hoy corriera» no los cuenta mientras no se elija.
 */
export function cuerpoDeSimulacion(b: BorradorDeEstrategia): EstrategiaEnBorradorBody | null {
  const group = grupoParaContar(b);
  if (!group || !b.permiso) return null;
  if (NUMERO_DEL_METODO[b.metodo] && configDelBorrador(b) == null) return null;
  return {
    group,
    strategy: b.metodo,
    strategyConfig: configDelBorrador(b),
    mode: b.permiso.mode,
    askAboveChangePct: b.permiso.askAboveChangePct,
    onOverlap: b.onOverlap ?? 'DEJARLOS',
  };
}

/** El cuerpo del alta. Sólo se llama con `faltaEnElPaso(b, 3)` vacío. */
export function cuerpoDeAlta(b: BorradorDeEstrategia): CreateAppliedStrategy {
  const group = grupoDelBorrador(b);
  if (!group || !b.permiso) throw new Error('El borrador no está completo');
  const conHora = b.cadencia === 'DAILY' || b.cadencia === 'WEEKLY';
  return {
    name: b.nombre.trim(),
    group,
    strategy: b.metodo,
    strategyConfig: configDelBorrador(b),
    mode: b.permiso.mode,
    askAboveChangePct: b.permiso.askAboveChangePct,
    cadence: b.cadencia,
    cadenceHour: conHora ? b.hora : null,
    cadenceMinute: conHora ? b.minuto : 0,
    cadenceDayOfWeek: b.cadencia === 'WEEKLY' ? b.dia : null,
    validUntil: b.hastaCuando ? new Date(finDelDia(b.hastaCuando)).toISOString() : null,
    ...(b.onOverlap && { onOverlap: b.onOverlap }),
  };
}

/**
 * Los métodos que ofrece el paso 2 (`METODOS`): una fórmula propia no. Vive acá
 * porque `paso-2-metodo` importa de este archivo (importarla de allá haría un
 * ciclo). Escaneo previo Nº 18: que no se desincronice lo fija un test
 * (`METODOS` recorre exactamente estas claves, en este orden).
 */
export const METODOS_OFRECIDOS: readonly StrategyKind[] = [
  'MATCH_LOWEST',
  'UNDERCUT_PERCENT',
  'MARGIN_TARGET',
  'SMART_PRICING',
  'DISCOUNT_PERCENT',
];

/**
 * Análisis → «Detalle por producto» (plan 3, T5c · P3-D7): el panel de un
 * producto y «Crear una estrategia con estos N» abren el asistente con esos
 * productos y, si se eligió, el método con su número. Nada se aplica en el acto.
 */
export function borradorDesdeElAnalisis(args: {
  nombre: string;
  skus: string[];
  metodo?: string | null;
  /** El `StrategyConfig` del panel (`targetMargin`, `undercutPercent`…): se lee el número del método. */
  config?: object | null;
}): Partial<BorradorDeEstrategia> {
  const metodo = METODOS_OFRECIDOS.includes(args.metodo as StrategyKind)
    ? (args.metodo as StrategyKind)
    : BORRADOR_VACIO.metodo;
  const n = NUMERO_DEL_METODO[metodo];
  const numero = n
    ? (args.config as Record<string, unknown> | null | undefined)?.[n.key]
    : undefined;
  return {
    nombre: args.nombre,
    forma: 'PRODUCTOS',
    skus: [...args.skus],
    metodo,
    valor: typeof numero === 'number' ? String(numero) : valorInicial(metodo),
  };
}

/**
 * ¿Ya venció? Cerrada por el barrido (`closedAt`) o con la fecha de fin pasada.
 * Una vencida no se reabre (plan 2, P2-D4: «Ya venció: creá una nueva»); se
 * duplica (T6b). Tiene que dar lo mismo que `yaVencio` del plan 2: hoy coinciden.
 */
export function estaVencida(
  app: Pick<AppliedStrategy, 'closedAt' | 'validUntil'>,
  ahora: Date = new Date()
): boolean {
  return app.closedAt != null || (app.validUntil != null && new Date(app.validUntil) < ahora);
}

/** Lo que el asistente no ofrece y la copia no puede llevar (se dice, no se pierde en silencio). */
const FILTROS_QUE_SE_COPIAN = new Set(['categories', 'brands']);

const listaDeTextos = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

/**
 * «Duplicar» (plan 3, Tarea 6b; decisión del 26-sep): el borrador del asistente
 * con todo copiado de una estrategia que ya existe — a quién alcanza (categorías
 * y marcas, o los productos elegidos), las excepciones, cómo calcula y su número,
 * el permiso con su umbral y cada cuánto revisa. Sale **sin nombre** y **sin
 * fecha de fin**: son lo único que el asistente pide.
 */
export function borradorDesdeLaEstrategia(app: AppliedStrategy): Partial<BorradorDeEstrategia> {
  const g = app.group;
  const manual = g.kind === 'MANUAL';
  const filtro = (g.filter ?? {}) as Record<string, unknown>;
  // Una fórmula propia no se ofrece en el paso 2: la copia arranca en el de siempre.
  const metodo: StrategyKind =
    app.strategy === 'CUSTOM_FORMULA' ? BORRADOR_VACIO.metodo : app.strategy;
  const n = NUMERO_DEL_METODO[metodo];
  const numero = n ? (app.strategyConfig ?? {})[n.key] : undefined;
  const supervisada = app.mode === 'SUPERVISED';
  return {
    nombre: '',
    forma: manual ? 'PRODUCTOS' : 'CATEGORIA_Y_MARCA',
    categorias: manual ? [] : listaDeTextos(filtro.categories),
    marcas: manual ? [] : listaDeTextos(filtro.brands),
    skus: manual ? [...g.skus] : [],
    excluidos: manual ? [] : [...(g.excludedSkus ?? [])],
    metodo,
    valor: typeof numero === 'number' ? String(numero) : valorInicial(metodo),
    // `AUTO_WITH_RULES` (legado) se lee como AUTO, igual que en la tarjeta.
    permiso: {
      mode: supervisada ? 'SUPERVISED' : 'AUTO',
      askAboveChangePct: supervisada ? null : (app.askAboveChangePct ?? null),
    },
    cadencia: app.cadence,
    hora: app.cadenceHour ?? BORRADOR_VACIO.hora,
    minuto: app.cadenceMinute,
    dia: app.cadenceDayOfWeek ?? BORRADOR_VACIO.dia,
    hastaCuando: null,
    onOverlap: null,
  };
}

/** Qué tenía la original que la copia no lleva, dicho para la persona. Vacío = va todo. */
export function loQueNoSeCopia(app: AppliedStrategy): string[] {
  const fuera: string[] = [];
  if (app.group.kind !== 'MANUAL') {
    const otros = Object.keys(app.group.filter ?? {}).filter((k) => !FILTROS_QUE_SE_COPIAN.has(k));
    if (otros.length > 0) {
      fuera.push(
        'los filtros por precio, margen, stock o competencia (ya no se eligen acá: viven en Reglas)'
      );
    }
  }
  if (app.strategy === 'CUSTOM_FORMULA') {
    fuera.push('la fórmula propia (el asistente no la ofrece: la copia arranca en otro método)');
  }
  return fuera;
}
